import swal from "sweetalert";

interface PopconfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClass?: string;
  cancelClass?: string;
  icon?: string;
  dangerMode?: boolean;
}

class Popconfirm {
  private static instance: Popconfirm | null = null;
  private options: PopconfirmOptions;

  constructor(options: PopconfirmOptions = {}) {
    this.options = {
      title: "Are you sure?",
      message: "",
      confirmText: "Yes",
      cancelText: "No",
      confirmClass: "sn-btn sn-btn-sm sn-btn-red",
      cancelClass: "sn-btn sn-btn-sm sn-btn-silver",
      icon: "warning",
      dangerMode: true,
      ...options
    };

    this.init();
  }

  private init(): void {
    // Use event delegation to handle dynamically added elements
    $('.popconfirm').off('click.popconfirm').on('click.popconfirm', this.handleClick.bind(this));
  }

  private handleClick(e: JQuery.ClickEvent): void {
    e.preventDefault();
    e.stopPropagation();

    const $target = $(e.currentTarget);
    const target = e.currentTarget;

    // Get custom options from data attributes
    const customOptions = this.getCustomOptions($target);
    const mergedOptions = { ...this.options, ...customOptions };

    this.showConfirmation(target, mergedOptions);
  }

  private getCustomOptions($target: JQuery): Partial<PopconfirmOptions> {
    const options: Partial<PopconfirmOptions> = {};

    if ($target.data("confirm-title")) {
      options.title = $target.data("confirm-title");
    }
    if ($target.data("confirm-message")) {
      options.message = $target.data("confirm-message");
    }
    if ($target.data("confirm-text")) {
      options.confirmText = $target.data("confirm-text");
    }
    if ($target.data("cancel-text")) {
      options.cancelText = $target.data("cancel-text");
    }
    if ($target.data("confirm-class")) {
      options.confirmClass = $target.data("confirm-class");
    }
    if ($target.data("cancel-class")) {
      options.cancelClass = $target.data("cancel-class");
    }
    if ($target.data("icon")) {
      options.icon = $target.data("icon");
    }
    if (typeof $target.data("danger-mode") !== "undefined") {
      options.dangerMode = $target.data("danger-mode");
    }

    return options;
  }

  private showConfirmation(target: HTMLElement, options: PopconfirmOptions): void {
    swal({
      title: options.title,
      text: options.message,
      icon: options.icon,
      closeOnClickOutside: false,
      className: "sn-m-modal",
      buttons: {
        cancel: {
          text: options.cancelText,
          value: null,
          visible: true,
          className: options.cancelClass,
          closeModal: true
        },
        confirm: {
          text: options.confirmText,
          value: true,
          visible: true,
          className: options.confirmClass,
          closeModal: true
        }
      },
      dangerMode: options.dangerMode,
    }).then((willConfirm) => {
      if (willConfirm) {
        this.executeAction(target);
      }
      // If rejected, do nothing - the dialog will show again on next click
    });
  }

  private executeAction(target: HTMLElement): void {
    // Handle different types of elements
    if (target.tagName === 'FORM') {
      // For forms, submit the form
      (target as HTMLFormElement).submit();
    } else if (target.tagName === 'A') {
      // For links, navigate to the href
      const href = (target as HTMLAnchorElement).href;
      if (href && href !== '#' && href !== 'javascript:void(0)') {
        window.location.href = href;
      }
      // @ts-ignore
    } else if (target.tagName === 'BUTTON' || target.type === 'submit') {
      // For buttons, trigger the click event without prevention
      const form = target.closest('form');
      if (form) {
        // If it's a submit button in a form, submit the form
        (form as HTMLFormElement).submit();
      } else {
        // Otherwise, trigger the original click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        target.dispatchEvent(clickEvent);
      }
      $(target).addClass('disabled not-allowed').prop('disabled', true);
    } else {
      // For other elements, trigger a click event
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      target.dispatchEvent(clickEvent);
    }
  }

  // Static method to create a global instance
  static init(options?: PopconfirmOptions): Popconfirm {
    if (!Popconfirm.instance) {
      Popconfirm.instance = new Popconfirm(options);
    }
    return Popconfirm.instance;
  }

  // Method to destroy the instance and clean up event listeners
  destroy(): void {
    $(document).off('click.popconfirm', '.popconfirm');
    Popconfirm.instance = null;
  }

  // Method to update options
  updateOptions(options: Partial<PopconfirmOptions>): void {
    this.options = { ...this.options, ...options };
  }
}

// Export both the class and a convenience function
export { Popconfirm, PopconfirmOptions };

export function LoadPage(options?: PopconfirmOptions): Popconfirm {
  return Popconfirm.init(options);
}
